/** The window's four tabs: Live match, Scorecard, Picks and Gist. */
import type { ReactNode } from "react";

import { Badge, Table, TBody, TD, TH, THead, TR } from "@/components/ui";
import { cn } from "@/lib/cn";
import { DASH, formatInt } from "@/lib/format";

import {
  priceINR,
  signedINR,
  timeIST,
  type Cell,
  type Feed,
  type Item,
  type MarketFilter,
  type ModelFilter,
  type Pick,
  type Score,
  type Summary,
  type Who,
} from "./api";
import { Mascot } from "./Mascot";

const NAME: Record<Who, string> = { algo: "ALGO AI", ai: "AI Trader" };
const MARKETS: { value: MarketFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "index", label: "Index" },
  { value: "stocks", label: "Stocks" },
  { value: "mcx", label: "MCX" },
];
const MODELS: { value: ModelFilter; label: string }[] = [
  { value: "both", label: "Both" },
  { value: "algo", label: "ALGO AI" },
  { value: "ai", label: "AI Trader" },
];
const CELL = "h-8 px-2 text-[13px]";
const HEAD = "h-8 px-2";

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "cursor-pointer rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors",
        on ? "border-brand bg-brand text-brand-fg" : "border-border bg-surface text-muted hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}

function SideBadge({ action }: { action: string | null | undefined }) {
  if (!action) return <span className="text-subtle">{DASH}</span>;
  const tone = action === "BUY" ? "up" : action === "SELL" ? "down" : "neutral";
  return (
    <Badge pill tone={tone} className="px-[7px] py-px text-[11px] font-bold">
      {action}
    </Badge>
  );
}

function Avatar({ who, mood }: { who: Who | "mascot"; mood?: "watching" | "happy" | "sad" }) {
  if (who === "mascot") return <Mascot mood={mood} className="size-7 shrink-0" />;
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-7 shrink-0 place-items-center rounded-full font-extrabold text-white",
        who === "algo" ? "bg-[#1f56e0] text-[8px] tracking-tight" : "bg-[#6d3fe0] text-[10px]",
      )}
    >
      {who === "algo" ? "ALGO" : "AI"}
    </span>
  );
}

function Message({
  who,
  mood,
  time,
  note,
  children,
}: {
  who: Who | "mascot";
  mood?: "watching" | "happy" | "sad";
  time?: string;
  note?: ReactNode;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-2">
      <Avatar who={who} mood={mood} />
      <div
        className={cn(
          "min-w-0 rounded-[4px_12px_12px_12px] border border-border bg-surface px-2.5 py-1.5 text-[13.5px] break-words text-fg",
          who === "algo" && "border-l-[3px] border-l-[#1f56e0] dark:border-l-[#6f9bff]",
          who === "ai" && "border-l-[3px] border-l-[#6d3fe0] dark:border-l-[#b39dff]",
        )}
      >
        {children}
        {note || time ? (
          <small className="block text-[11.5px] text-muted">
            {time ? <span className="num">{time}</span> : null}
            {note && time ? " · " : null}
            {note}
          </small>
        ) : null}
      </div>
    </li>
  );
}

function levels(i: Item): string {
  const parts: string[] = [];
  if (i.price != null) parts.push(priceINR(i.price));
  if (i.stop != null) parts.push(`stop ${priceINR(i.stop)}`);
  if (i.target != null) parts.push(`target ${priceINR(i.target)}`);
  return parts.join(" · ");
}

function ExitWords({ i, who }: { i: Item; who: Who }) {
  const result = i.pnl != null ? signedINR(i.pnl) : DASH;
  if (i.result === "target")
    return (
      <>
        <b>Target hit!</b> {NAME[who]} closes {i.instrument}
        {i.price != null ? ` at ${priceINR(i.price)}` : ""}: <span className="num font-semibold whitespace-nowrap text-up">{result}</span>.
      </>
    );
  if (i.result === "stop")
    return (
      <>
        <b>Stop-loss hit.</b> {NAME[who]} {i.instrument} closed: <span className="num font-semibold whitespace-nowrap text-down">{result}</span>.
      </>
    );
  return (
    <>
      <b>{i.instrument}</b> closed{i.price != null ? ` at ${priceINR(i.price)}` : ""}:{" "}
      <span className={cn("num font-semibold whitespace-nowrap", (i.pnl ?? 0) >= 0 ? "text-up" : "text-down")}>{result}</span>
    </>
  );
}

function Line({ i }: { i: Item }) {
  const time = timeIST(i.ts);
  const who = i.who ?? "algo";
  if (i.kind === "start")
    return (
      <Message who="mascot" time={time}>
        <b>Match on!</b> ALGO AI vs AI Trader.
      </Message>
    );
  if (i.kind === "skip")
    return (
      <Message who="ai" time={time} note={i.reason ? `Why: ${i.reason}` : null}>
        <SideBadge action="SKIP" /> <b>{i.instrument}</b> no trade
      </Message>
    );
  if (i.kind === "exit") {
    const mascot = i.result === "target" || i.result === "stop";
    if (mascot)
      return (
        <Message who="mascot" mood={i.result === "target" ? "happy" : "sad"} time={time}>
          <ExitWords i={i} who={who} />
        </Message>
      );
    return (
      <Message
        who={who}
        time={time}
        note={i.result === "signal" ? "It chose to exit." : "Closed at its exit time."}
      >
        <ExitWords i={i} who={who} />
      </Message>
    );
  }
  return (
    <Message who={who} time={time} note={i.reason ? (who === "ai" ? `Why: ${i.reason}` : i.reason) : null}>
      <SideBadge action={i.side} /> <b>{i.instrument}</b> {levels(i)}
    </Message>
  );
}

export function LiveTab({
  items,
  summary,
  market,
  model,
  onMarket,
  onModel,
}: {
  items: Item[];
  summary: Summary;
  market: MarketFilter;
  model: ModelFilter;
  onMarket: (m: MarketFilter) => void;
  onModel: (m: ModelFilter) => void;
}) {
  // the API sends newest first; the window reads like a chat, morning at the top
  const lines = [...items].reverse();
  return (
    <>
      <div className="sticky -top-2.5 z-[1] -mx-3 -mt-2.5 mb-2 space-y-1.5 bg-bg px-3 pt-2.5 pb-2">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Market">
          {MARKETS.map((m) => (
            <Chip key={m.value} on={market === m.value} onClick={() => onMarket(m.value)}>
              {m.label}
            </Chip>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Model">
          {MODELS.map((m) => (
            <Chip key={m.value} on={model === m.value} onClick={() => onModel(m.value)}>
              {m.label}
            </Chip>
          ))}
        </div>
      </div>
      {lines.length ? (
        <ul aria-label="Live match" className="space-y-2">
          {lines.map((i) => (
            <Line key={i.id} i={i} />
          ))}
        </ul>
      ) : (
        <ul aria-label="Live match" className="space-y-2">
          <Message who="mascot">{summary.today_note ?? "No paper trades to show yet today."}</Message>
        </ul>
      )}
    </>
  );
}

function money(n: number): ReactNode {
  return <span className={n > 0 ? "text-up" : n < 0 ? "text-down" : undefined}>{signedINR(n)}</span>;
}

function best(s: Score): ReactNode {
  if (!s.best) return DASH;
  return (
    <>
      <span className="block font-sans">{s.best.symbol}</span>
      {signedINR(s.best.net)}
    </>
  );
}

function Mark({ c, k }: { c: Cell; k: string }) {
  const figure = k === "trades" ? formatInt(c.value) : k === "t" ? (c.value?.toFixed(2) ?? DASH) : null;
  return (
    <span className={cn("font-bold", c.ok ? "text-up" : "text-down")}>
      {figure ?? (c.ok ? "✓" : "✗")}
      <span className="sr-only">{c.ok ? " passed" : " not yet"}</span>
    </span>
  );
}

function Versus({ first, children }: { first: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface">
      <Table>
        <THead>
          <tr>
            <TH className={HEAD}>{first}</TH>
            <TH numeric className={cn(HEAD, "normal-case")}>
              ALGO AI
            </TH>
            <TH numeric className={cn(HEAD, "normal-case")}>
              AI Trader
            </TH>
          </tr>
        </THead>
        <TBody>{children}</TBody>
      </Table>
    </div>
  );
}

function Row({ label, a, b }: { label: string; a: ReactNode; b: ReactNode }) {
  return (
    <TR>
      <TD className={cn(CELL, "whitespace-nowrap")}>{label}</TD>
      <TD numeric className={CELL}>
        {a}
      </TD>
      <TD numeric className={CELL}>
        {b}
      </TD>
    </TR>
  );
}

function Note({ children }: { children: ReactNode }) {
  return <p className="mb-2 text-[12.5px] text-muted">{children}</p>;
}

export function ScorecardTab({ data, note }: { data: Feed["scorecard"]; note?: string | null }) {
  if (!data) return null;
  const { algo, ai } = data.today;
  return (
    <div className="space-y-2.5">
      {note ? <Note>{note}</Note> : null}
      <Versus first="Today">
        <Row label="Net after costs" a={money(algo.net)} b={money(ai.net)} />
        <Row label="Trades closed" a={formatInt(algo.closed)} b={formatInt(ai.closed)} />
        <Row label="Wins" a={formatInt(algo.wins)} b={formatInt(ai.wins)} />
        <Row label="Open now" a={formatInt(algo.open)} b={formatInt(ai.open)} />
        <Row label="Open P&L, before costs" a={money(algo.open_net)} b={money(ai.open_net)} />
        <Row label="Best trade" a={best(algo)} b={best(ai)} />
      </Versus>
      <div className="text-[12.5px] font-bold text-fg">{data.test.headline}</div>
      <Versus first="To be chosen">
        {data.test.checks.map((c) => (
          <Row key={c.key} label={c.label} a={<Mark c={c.algo} k={c.key} />} b={<Mark c={c.ai} k={c.key} />} />
        ))}
      </Versus>
    </div>
  );
}

export function PicksTab({ picks, note }: { picks: Pick[]; note?: string | null }) {
  if (!picks.length)
    return <Note>{note ?? "No picks to show yet today."}</Note>;
  return (
    <div className="space-y-2">
      <div className="text-[12.5px] font-bold text-fg">Today's picks</div>
      <Versus first="Instrument">
        {picks.map((p) => (
          <Row key={p.symbol} label={p.symbol} a={<SideBadge action={p.algo} />} b={<SideBadge action={p.ai} />} />
        ))}
      </Versus>
      <p className="text-[12.5px] text-muted">Filter by market and model in Live match.</p>
    </div>
  );
}

export function GistTab({ gist }: { gist: Feed["gist"] }) {
  if (!gist) return null;
  return (
    <ul className="space-y-2">
      <Message who="mascot" note="Built from today's paper figures.">
        <b>{gist.title}</b>
        {gist.lines.map((l) => (
          <span key={l} className="mt-1 block">
            {l}
          </span>
        ))}
      </Message>
    </ul>
  );
}
